(() => {
  'use strict';

  // ---------- Config ----------
  const MOBILE_MQ = '(max-width: 1023px)';
  const ROOT_SELECTOR = '#ProductGridContainer';
  const CARD_SELECTOR = '.card-wrapper';
  // Keep in sync with CSS `collection-gallery-hint-*` duration
  const HINT_DURATION_MS = 3000;
  // Figma mid-frame peek: ~107px on 187px card ≈ 0.57
  const HINT_PEEK_RATIO = 0.45;
  // Commit sooner than midpoint — ~half of prior 15% for a snappier swipe
  const SWIPE_COMMIT_RATIO = 0.15;
  // Custom settle duration (~2× faster than native smooth ~280–300ms)
  const SETTLE_DURATION_MS = 140;
  const IO_ROOT_MARGIN = '200px 0px';
  const IDLE_FALLBACK_MS = 600;
  const PLACEHOLDER_SRC =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
  const SWATCH_SIZE_CLASSES = [
    '!tw-size-[22px]',
    'tw-size-[22px]',
    '!tw-size-[24px]',
    'tw-size-[24px]',
    'lg:!tw-size-[24px]',
    '!tw-border-[3px]',
    'tw-border-[3px]'
  ];

  // ---------- Shared state ----------
  let hintPlayed = false;
  let initialized = false;
  let galleryObserver = null;
  let resizeRaf = null;
  const galleryControllers = new WeakMap();
  const observedCards = new WeakSet();
  // All live gallery controllers, so window resize is handled by ONE
  // debounced listener instead of one `resize` listener per card.
  const activeControllers = new Set();
  const loggedEvents = new Set();

  const isMobile = () => window.matchMedia(MOBILE_MQ).matches;

  const scheduleIdle = cb =>
    typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback(() => cb(), { timeout: 400 })
      : window.requestAnimationFrame(() => cb());

  // Chrome runs timed-out idle callbacks off the idle queue, which a busy main
  // thread can starve for as long as the user keeps scrolling — cards then
  // never get their gallery. Safari's rIC is timer-backed and always fired,
  // which is why this only ever showed up in Chrome. The timer is the
  // guarantee; whichever of the two fires first wins.
  const scheduleCardWork = cb => {
    let ran = false;
    const run = () => {
      if (ran) return;
      ran = true;
      cb();
    };
    scheduleIdle(run);
    setTimeout(run, IDLE_FALLBACK_MS);
  };

  const pushEvent = (eventName, parameter) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'custom_event',
      event_name: eventName,
      parameter: JSON.stringify(parameter)
    });
  };

  const toItemUrl = href => {
    if (!href) return '';
    try {
      const path = new URL(href, window.location.origin).pathname.replace(
        /\/+$/,
        ''
      );
      return path || '';
    } catch {
      const raw = String(href).split('?')[0].replace(/\/+$/, '');
      return raw.startsWith('/') ? raw : `/${raw}`;
    }
  };

  const getCollectionHandle = card => {
    const fromCard = card?.closest('[data-collection]')?.dataset?.collection;
    if (fromCard) return fromCard;
    const match = window.location.pathname.match(/\/collections\/([^/]+)/);
    return match?.[1] || '';
  };

  const getItemNumber = card => {
    const item = card?.closest('.product-item, .grid__item');
    const grid =
      document.querySelector('#ProductGridContainer .product-grid') ||
      document.querySelector('#ProductGridContainer');
    if (!item || !grid) return undefined;
    const items = Array.from(
      grid.querySelectorAll(
        '.product-item.collection-all, .grid__item.product-item'
      )
    ).filter(el => el.querySelector('.card-wrapper'));
    const index = items.indexOf(item);
    return index >= 0 ? index + 1 : undefined;
  };

  const getCardEventParams = card => {
    const href =
      card
        ?.querySelector('a.card-information__wrapper-link')
        ?.getAttribute('href') ||
      card?.querySelector('[data-handle]')?.dataset?.handle ||
      card?.querySelector('.card-information__text a')?.getAttribute('href') ||
      card?.querySelector('.product-alt-colours a')?.getAttribute('href') ||
      '';

    let itemId = card?.dataset?.variantId || '';
    if (!itemId) {
      const qaEl = card?.querySelector('[data-quick-add-prod-info]');
      const raw = qaEl?.getAttribute('data-quick-add-prod-info');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          itemId = parsed?.variants?.[0]?.id || '';
        } catch {
          /* ignore */
        }
      }
    }
    if (!itemId) {
      itemId = card?.querySelector('[data-variant]')?.dataset?.variant || '';
    }

    const itemNumber = getItemNumber(card);
    const params = {
      from: 'collection_page',
      content_url: toItemUrl(href),
      item_id: itemId ? Number(itemId) || String(itemId) : '',
      product_collection: getCollectionHandle(card)
    };
    if (itemNumber != null) params.item_number = itemNumber;
    return params;
  };

  // Once per product (item_id) per event name
  const trackProductEvent = (eventName, card) => {
    const params = getCardEventParams(card);
    if (!params.item_id && !params.content_url) return;
    const key = `${eventName}:${params.item_id || params.content_url}`;
    if (loggedEvents.has(key)) return;
    loggedEvents.add(key);
    pushEvent(eventName, params);
  };

  const trackGallerySwipe = card => trackProductEvent('gallery_swipe', card);
  const trackColorsSwipe = card => trackProductEvent('colors_swipe', card);
  const trackColorsMoreClick = card => {
    const params = getCardEventParams(card);
    if (!params.item_id && !params.content_url) return;
    pushEvent('colors_more_click', params);
  };

  // ---------- Image helpers ----------
  const normalizeImage = item => {
    if (!item) return null;
    if (typeof item === 'string') return { src: item, src2x: item };
    if (item.type === 'video') return null; // images-only for performance
    const src = item.src || item.url || '';
    return src ? { src, src2x: item.src2x || src } : null;
  };

  const parseGallery = value => {
    if (!value) return [];
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return Array.isArray(parsed)
        ? parsed.map(normalizeImage).filter(Boolean)
        : [];
    } catch {
      return [];
    }
  };

  const buildSrcSet = image => {
    if (!image?.src) return '';
    return image.src2x && image.src2x !== image.src
      ? `${image.src} 1x, ${image.src2x} 2x`
      : `${image.src} 1x`;
  };

  const createPicture = (
    image,
    alt,
    { eager = false, highPriority = false } = {}
  ) => {
    const picture = document.createElement('picture');
    picture.className = 'product-image';

    const source = document.createElement('source');
    source.media = '(min-width: 768px)';
    source.srcset = buildSrcSet({
      src: image.src2x || image.src,
      src2x: image.src2x || image.src
    });

    const img = document.createElement('img');
    img.className = 'product-image__img';
    img.alt = alt || '';
    img.decoding = 'async';
    img.draggable = false;

    if (eager) {
      img.loading = 'eager';
      // First slide of the first card is LCP — keep it highest priority.
      if (highPriority) img.fetchPriority = 'high';
      img.srcset = buildSrcSet(image);
      img.src = image.src;
    } else {
      img.loading = 'lazy';
      img.dataset.srcset = buildSrcSet(image);
      img.dataset.src = image.src;
      img.src = PLACEHOLDER_SRC;
    }

    picture.append(source, img);
    return picture;
  };

  const createSlide = (image, productUrl, alt, pictureOptions) => {
    const slide = document.createElement('div');
    slide.className = 'collection-card-gallery__slide';

    const link = document.createElement('a');
    link.href = productUrl || '#';
    link.className = 'card-information__wrapper-link tw-w-full';
    link.draggable = false;
    link.append(createPicture(image, alt, pictureOptions));

    slide.append(link);
    return slide;
  };

  const loadSlideImage = slide => {
    const img = slide?.querySelector('img');
    if (!img?.dataset.src) return;
    img.srcset = img.dataset.srcset || '';
    img.src = img.dataset.src;
    delete img.dataset.src;
    delete img.dataset.srcset;
  };

  const stripSwatchSizeClasses = root => {
    root
      ?.querySelectorAll(
        '.color_swatch_item-span, .product-color_swatch, .color_swatch_item'
      )
      .forEach(el => el.classList.remove(...SWATCH_SIZE_CLASSES));
  };

  // ---------- Gallery (per card) ----------
  const enhanceGallery = (card, { eagerSecond = false } = {}) => {
    const imgBox = card.querySelector('.img_box');
    if (!imgBox) return null;
    if (imgBox.dataset.collectionGalleryInit === '1') {
      return galleryControllers.get(imgBox) || null;
    }

    const images = parseGallery(imgBox.dataset.collectionGallery);
    if (images.length < 2) return null;

    const originalLink = imgBox.querySelector(
      'a.card-information__wrapper-link'
    );
    if (!originalLink) return null;

    let productUrl = originalLink.getAttribute('href') || '#';
    const alt =
      originalLink.querySelector('img')?.getAttribute('alt') ||
      card.querySelector('.card-information__text a')?.textContent?.trim() ||
      '';

    const badges = imgBox.querySelector(':scope > .tw-absolute');
    const quickAdd = imgBox.querySelector(':scope > .quick-add-block');
    const mobileBtn = imgBox.querySelector(':scope > .btn-wrap');

    const track = document.createElement('div');
    track.className = 'collection-card-gallery__track';

    const viewport = document.createElement('div');
    viewport.className = 'collection-card-gallery__viewport';
    viewport.append(track);

    const thumb = document.createElement('div');
    thumb.className = 'collection-card-gallery__scrollbar-thumb';
    const scrollbar = document.createElement('div');
    scrollbar.className = 'collection-card-gallery__scrollbar';
    scrollbar.append(thumb);

    imgBox.classList.add('collection-card-gallery');
    imgBox.dataset.collectionGalleryInit = '1';
    originalLink.remove();
    imgBox.querySelector('.spare-img')?.remove();
    imgBox.append(viewport, scrollbar);
    [badges, quickAdd, mobileBtn].forEach(el => el && imgBox.append(el));

    // Slide elements are cached once and reused on every scroll/drag/resize
    // frame instead of re-querying the DOM (was the hottest path before).
    let slideEls = [];
    const rebuildSlides = list => {
      track.innerHTML = '';
      slideEls = list.map((image, i) => {
        const slide = createSlide(image, productUrl, alt, {
          // First card: preload slide 2 immediately so the intro peek / first
          // swipe never waits on lazy load.
          eager: i === 0 || (eagerSecond && i === 1),
          highPriority: i === 0
        });
        track.append(slide);
        return slide;
      });
    };
    rebuildSlides(images);

    let index = 0;
    let hideBarTimer = null;
    let touchStartScroll = 0;
    let touchStartX = 0;
    let touchEndX = 0;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let didDrag = false;
    let isPointerDown = false;
    let isSettling = false;
    let settleTimer = null;
    let scrollAnimRaf = null;
    let dragMinScroll = 0;
    let dragMaxScroll = 0;
    let lastReportedIndex = 0;
    let isHinting = false;
    let hintTimers = [];
    let suppressProgressBar = false;
    let cachedCardWidth = 0;

    const measureCardWidth = () => {
      const cardEl = card.closest('.grid__item') || card;
      cachedCardWidth = Math.round(
        cardEl.getBoundingClientRect().width ||
          card.clientWidth ||
          imgBox.clientWidth ||
          0
      );
      return cachedCardWidth;
    };

    const syncSlideWidths = () => {
      const width = measureCardWidth();
      if (!width) return;
      viewport.style.setProperty(
        '--collection-gallery-slide-width',
        `${width}px`
      );
      const px = `${width}px`;
      slideEls.forEach(slide => {
        slide.style.flex = `0 0 ${px}`;
        slide.style.width = px;
        slide.style.minWidth = px;
        slide.style.maxWidth = px;
      });
    };

    const slideWidth = () =>
      cachedCardWidth || viewport.clientWidth || imgBox.clientWidth || 1;
    const getIndexFromScroll = () =>
      Math.round(viewport.scrollLeft / slideWidth());

    const resolveSettleIndex = () => {
      const width = slideWidth();
      const total = slideEls.length;
      if (!width || total < 2) return 0;

      const startIndex = Math.min(
        total - 1,
        Math.max(0, Math.round(touchStartScroll / width))
      );
      const deltaScroll = viewport.scrollLeft - touchStartScroll;
      const swipeDx = touchStartX - touchEndX;
      const commitPx = width * SWIPE_COMMIT_RATIO;

      let target = startIndex;
      if (deltaScroll >= commitPx || swipeDx >= commitPx)
        target = startIndex + 1;
      else if (deltaScroll <= -commitPx || swipeDx <= -commitPx)
        target = startIndex - 1;

      return Math.min(total - 1, Math.max(0, target));
    };

    const updateThumb = () => {
      const total = slideEls.length || 1;
      thumb.style.width = `${100 / total}%`;
      // During CSS intro hint the thumb is animated — don't fight it with inline transform.
      if (isHinting) return;
      const maxScroll = Math.max(
        viewport.scrollWidth - viewport.clientWidth,
        1
      );
      const progress = viewport.scrollLeft / maxScroll;
      thumb.style.transform = `translateX(${progress * (total - 1) * 100}%)`;
    };

    const preloadAround = activeIndex => {
      loadSlideImage(slideEls[activeIndex]);
      loadSlideImage(slideEls[activeIndex + 1]);
      loadSlideImage(slideEls[activeIndex - 1]);
    };

    const clearHintTimers = () => {
      hintTimers.forEach(clearTimeout);
      hintTimers = [];
    };

    const stopHint = ({ settle = true } = {}) => {
      if (!isHinting && !imgBox.classList.contains('is-hinting')) return;
      isHinting = false;
      clearHintTimers();
      imgBox.classList.remove('is-hinting', 'is-interacting');
      imgBox.style.removeProperty('--hint-peek');
      if (settle) {
        cancelScrollAnim();
        viewport.scrollTo({ left: 0, behavior: 'auto' });
        index = 0;
        updateThumb();
      }
    };

    const cancelScrollAnim = () => {
      if (scrollAnimRaf != null) {
        cancelAnimationFrame(scrollAnimRaf);
        scrollAnimRaf = null;
      }
    };

    const animateScrollTo = (targetLeft, duration) => {
      cancelScrollAnim();
      const startLeft = viewport.scrollLeft;
      const delta = targetLeft - startLeft;
      if (Math.abs(delta) < 1 || duration <= 0) {
        viewport.scrollLeft = targetLeft;
        updateThumb();
        return;
      }
      const startTime = performance.now();
      const easeOutCubic = t => 1 - (1 - t) ** 3;
      const step = now => {
        const t = Math.min(1, (now - startTime) / duration);
        viewport.scrollLeft = startLeft + delta * easeOutCubic(t);
        updateThumb();
        if (t < 1) scrollAnimRaf = requestAnimationFrame(step);
        else scrollAnimRaf = null;
      };
      scrollAnimRaf = requestAnimationFrame(step);
    };

    const goTo = (
      nextIndex,
      { smooth = false, user = false, keepBar = false } = {}
    ) => {
      const total = slideEls.length;
      if (total < 2) return;

      // Hard bounds — no wrap / loop
      index = Math.min(total - 1, Math.max(0, nextIndex));
      const allowBar = user || keepBar || isHinting;
      if (!allowBar) suppressProgressBar = true;

      const targetLeft = index * slideWidth();
      if (smooth) {
        animateScrollTo(targetLeft, SETTLE_DURATION_MS);
      } else {
        cancelScrollAnim();
        viewport.scrollTo({ left: targetLeft, behavior: 'auto' });
      }
      preloadAround(index);
      updateThumb();

      if (!allowBar) {
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            suppressProgressBar = false;
          })
        );
      }
      if (user && !isHinting && index !== lastReportedIndex) {
        trackGallerySwipe(card);
        lastReportedIndex = index;
      }
    };

    const showBar = () => {
      imgBox.classList.add('is-interacting');
      clearTimeout(hideBarTimer);
      updateThumb();
    };

    const hideBarSoon = () => {
      if (isHinting) return;
      clearTimeout(hideBarTimer);
      hideBarTimer = setTimeout(
        () => imgBox.classList.remove('is-interacting'),
        400
      );
    };

    const clampDragScroll = () => {
      // One gesture / settle can travel at most one slide — kill multi-photo flings.
      if ((!isPointerDown && !isSettling) || isHinting) return;
      const left = viewport.scrollLeft;
      if (left < dragMinScroll) viewport.scrollLeft = dragMinScroll;
      else if (left > dragMaxScroll) viewport.scrollLeft = dragMaxScroll;
    };

    const endSettle = () => {
      isSettling = false;
      clearTimeout(settleTimer);
      settleTimer = null;
      cancelScrollAnim();
      viewport.style.scrollSnapType = '';
    };

    const onScroll = () => {
      clampDragScroll();
      if (isHinting) return updateThumb();

      // Progress bar only while the gallery is actively scrolling, not on tap/click.
      if (suppressProgressBar) {
        updateThumb();
      } else {
        showBar();
        hideBarSoon();
      }

      const nextIndex = getIndexFromScroll();
      if (nextIndex !== index) {
        index = nextIndex;
        preloadAround(index);
        if (index !== lastReportedIndex) {
          trackGallerySwipe(card);
          lastReportedIndex = index;
        }
      }
    };

    const settle = () => {
      if (isHinting) return;
      const total = slideEls.length;
      if (total < 2) return;

      const width = slideWidth();
      const left = viewport.scrollLeft;
      const target = resolveSettleIndex();
      const targetLeft = target * width;

      // Freeze current offset to kill native momentum, then ease to target.
      // Disable CSS snap while animating — otherwise proximity snap fights JS
      // and the card "springs" into place.
      viewport.style.scrollSnapType = 'none';
      viewport.scrollTo({ left, behavior: 'auto' });

      dragMinScroll = Math.min(left, targetLeft);
      dragMaxScroll = Math.max(left, targetLeft);
      isSettling = true;
      clearTimeout(settleTimer);

      goTo(target, { smooth: true, keepBar: true });

      const distance = Math.abs(left - targetLeft);
      settleTimer = setTimeout(
        () => {
          goTo(target, { smooth: false, keepBar: true });
          endSettle();
        },
        distance < 2 ? 0 : SETTLE_DURATION_MS + 20
      );
    };

    // Pointer Events already fire for touch on every modern mobile browser,
    // so separate touchstart/touchmove/touchend listeners are dropped —
    // previously every swipe was being processed twice (once per event type).
    const onPointerDown = event => {
      if (isHinting) stopHint();
      endSettle();
      pointerStartX = touchStartX = touchEndX = event.clientX;
      pointerStartY = event.clientY;
      didDrag = false;
      isPointerDown = true;
      touchStartScroll = viewport.scrollLeft;

      const width = slideWidth();
      const total = slideEls.length;
      const startIndex = Math.min(
        total - 1,
        Math.max(0, Math.round(touchStartScroll / width))
      );
      dragMinScroll = Math.max(0, (startIndex - 1) * width);
      dragMaxScroll = Math.min((total - 1) * width, (startIndex + 1) * width);
    };

    const onPointerMove = event => {
      touchEndX = event.clientX;
      if (
        Math.abs(event.clientX - pointerStartX) > 8 ||
        Math.abs(event.clientY - pointerStartY) > 8
      ) {
        didDrag = true;
      }
      clampDragScroll();
    };

    const onPointerUp = () => {
      isPointerDown = false;
      settle();
      hideBarSoon();
    };

    const onWheel = () => {
      if (isHinting) stopHint();
    };

    const onClickCapture = event => {
      if (!didDrag) return;
      event.preventDefault();
      event.stopPropagation();
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('pointerdown', onPointerDown, { passive: true });
    viewport.addEventListener('pointermove', onPointerMove, { passive: true });
    viewport.addEventListener('pointerup', onPointerUp, { passive: true });
    viewport.addEventListener('pointercancel', onPointerUp, { passive: true });
    viewport.addEventListener('wheel', onWheel, { passive: true });
    viewport.addEventListener('click', onClickCapture, true);

    const handleResize = () => {
      syncSlideWidths();
      goTo(index, { smooth: false });
    };

    syncSlideWidths();
    goTo(0, { smooth: false });
    requestAnimationFrame(() => {
      syncSlideWidths();
      goTo(0, { smooth: false });
    });

    const controller = {
      imgBox,
      handleResize,
      setImages(nextImages, productHref) {
        const list = Array.isArray(nextImages)
          ? nextImages.map(normalizeImage).filter(Boolean)
          : [];
        if (list.length < 2) {
          destroyGallery(imgBox, list[0]?.src, productHref);
          return;
        }

        if (productHref) productUrl = productHref;
        stopHint({ settle: false });
        rebuildSlides(list);
        lastReportedIndex = 0;
        syncSlideWidths();
        goTo(0, { smooth: false });
      },
      resetToFirst() {
        goTo(0, { smooth: false });
      },
      playHint() {
        if (hintPlayed || !isMobile() || isHinting || slideEls.length < 2)
          return;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          hintPlayed = true;
          return;
        }

        const rect = imgBox.getBoundingClientRect();
        if (rect.top > window.innerHeight * 0.85 || rect.bottom < 0) return;

        let ball = imgBox.querySelector('.collection-card-gallery__hint-ball');
        if (!ball) {
          ball = document.createElement('span');
          ball.className = 'collection-card-gallery__hint-ball';
          ball.setAttribute('aria-hidden', 'true');
          // Anchor (transform) + ring (width morph) stay separate so -50%
          // centering never fights the capsule stretch.
          const ring = document.createElement('span');
          ring.className = 'collection-card-gallery__hint-ball-ring';
          ball.append(ring);
          imgBox.append(ball);
        }

        const width = slideWidth();
        const peek = Math.round(width * HINT_PEEK_RATIO);
        imgBox.style.setProperty('--hint-peek', `${peek}px`);

        hintPlayed = true;
        isHinting = true;
        cancelScrollAnim();
        viewport.scrollTo({ left: 0, behavior: 'auto' });
        index = 0;
        loadSlideImage(slideEls[0]);
        loadSlideImage(slideEls[1]);
        thumb.style.transform = '';
        updateThumb();

        // CSS drives ball + track peek; JS only toggles the class once.
        imgBox.classList.add('is-hinting', 'is-interacting');

        let hintFinished = false;
        const finishHint = () => {
          if (hintFinished) return;
          hintFinished = true;
          ball.removeEventListener('animationend', onBallEnd);
          stopHint({ settle: true });
          preloadAround(0);
        };
        const onBallEnd = event => {
          if (event.animationName !== 'collection-gallery-hint-ball') return;
          finishHint();
        };
        ball.addEventListener('animationend', onBallEnd);
        hintTimers.push(setTimeout(finishHint, HINT_DURATION_MS + 80));
      },
      destroy() {
        stopHint({ settle: false });
        endSettle();
        activeControllers.delete(controller);
        viewport.removeEventListener('scroll', onScroll);
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', onPointerUp);
        viewport.removeEventListener('pointercancel', onPointerUp);
        viewport.removeEventListener('wheel', onWheel);
        viewport.removeEventListener('click', onClickCapture, true);
      }
    };

    activeControllers.add(controller);
    galleryControllers.set(imgBox, controller);
    return controller;
  };

  const destroyGallery = (imgBox, fallbackUrl, productHref) => {
    const controller = galleryControllers.get(imgBox);
    controller?.destroy();
    galleryControllers.delete(imgBox);
    if (!imgBox?.classList.contains('collection-card-gallery')) return;

    const slideImg = imgBox.querySelector(
      '.collection-card-gallery__slide img'
    );
    const href =
      productHref ||
      imgBox.querySelector('.collection-card-gallery__slide a')?.href ||
      '#';
    const alt = (slideImg?.alt || '').replace(/"/g, '&quot;');
    const url = fallbackUrl || slideImg?.currentSrc || slideImg?.src || '';

    const badges = imgBox.querySelector(':scope > .tw-absolute');
    const quickAdd = imgBox.querySelector(':scope > .quick-add-block');
    const mobileBtn = imgBox.querySelector(':scope > .btn-wrap');

    imgBox.classList.remove(
      'collection-card-gallery',
      'is-interacting',
      'is-hinting'
    );
    delete imgBox.dataset.collectionGalleryInit;
    imgBox
      .querySelectorAll(
        '.collection-card-gallery__viewport, .collection-card-gallery__scrollbar, .collection-card-gallery__hint-ball, .collection-card-gallery__hint'
      )
      .forEach(el => el.remove());

    if (!url || url.startsWith('data:')) return;

    const link = document.createElement('a');
    link.href = href;
    link.className = 'card-information__wrapper-link tw-w-full';
    link.innerHTML = `
      <picture class="product-image">
        <source srcset="${url}" media="(min-width: 768px)">
        <img class="product-image__img" srcset="${url}" src="${url}" alt="${alt}" loading="lazy">
      </picture>
      <div class="spare-img">
        <picture>
          <source srcset="${url}" media="(min-width: 768px)">
          <img srcset="${url}" src="${url}" alt="${alt}">
        </picture>
      </div>
    `;
    imgBox.prepend(link);
    [badges, quickAdd, mobileBtn].forEach(el => el && imgBox.append(el));
  };

  // ---------- Swatches ----------
  const getSwatchStep = scroller => {
    const first = scroller.querySelector('a:not(.additional-color)');
    if (!first) return 28;
    const style = getComputedStyle(scroller);
    const gap = parseFloat(style.columnGap || style.gap || '8') || 8;
    return first.getBoundingClientRect().width + gap;
  };

  const enhanceSwatches = card => {
    const swatchRoot = card.querySelector(
      '.product-alt-colours.alt-colours__grid'
    );
    if (!swatchRoot || swatchRoot.dataset.collectionSwatchInit === '1') return;

    const links = Array.from(
      swatchRoot.querySelectorAll('a:not(.additional-color)')
    );
    if (!links.length) return;

    links.forEach((link, i) => {
      link.classList.remove('hidden', 'tw-hidden');
      if (i === 0 || link.querySelector('.color_active'))
        link.classList.add('color_active');
    });

    stripSwatchSizeClasses(swatchRoot);
    const additional = swatchRoot.querySelector('.additional-color');
    if (additional) additional.hidden = true;

    let scroller = swatchRoot.querySelector('.product-alt-colours__scroller');
    if (!scroller) {
      scroller = document.createElement('div');
      scroller.className = 'product-alt-colours__scroller';
      links.forEach(link => scroller.append(link));
      swatchRoot.prepend(scroller);
    }

    let arrow = swatchRoot.querySelector('[data-collection-swatch-arrow]');
    if (!arrow) {
      arrow = document.createElement('button');
      arrow.type = 'button';
      arrow.className = 'collection-swatch-arrow';
      arrow.setAttribute('data-collection-swatch-arrow', '');
      arrow.setAttribute(
        'aria-label',
        document.querySelector(ROOT_SELECTOR)?.dataset.moreColorsLabel ||
          'More colors'
      );
      arrow.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="9" height="10" viewBox="0 0 9 10" fill="none" aria-hidden="true"><path d="M3.25 1.5 6.75 5l-3.5 3.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      swatchRoot.append(arrow);
    }

    const syncArrow = () => {
      const canScroll = scroller.scrollWidth > scroller.clientWidth + 2;
      const atEnd =
        scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2;
      const visible = canScroll && !atEnd;
      arrow.classList.toggle('is-visible', visible);
      arrow.hidden = !visible;
    };

    // Arrow scroll must not also fire colors_swipe
    let suppressColorsSwipe = false;

    arrow.onclick = event => {
      event.preventDefault();
      event.stopPropagation();
      suppressColorsSwipe = true;
      scroller.scrollBy({ left: getSwatchStep(scroller), behavior: 'smooth' });
      trackColorsMoreClick(card);
      setTimeout(() => {
        suppressColorsSwipe = false;
        syncArrow();
      }, 280);
    };

    scroller.addEventListener(
      'scroll',
      () => {
        if (!suppressColorsSwipe) trackColorsSwipe(card);
        syncArrow();
      },
      { passive: true }
    );

    swatchRoot.dataset.collectionSwatchInit = '1';
    syncArrow();
    requestAnimationFrame(syncArrow);
  };

  const applyColorGallery = (card, swatchLink) => {
    const imgBox = card.querySelector('.img_box');
    if (!imgBox) return;

    const images = parseGallery(
      swatchLink.dataset.collectionGallery || imgBox.dataset.collectionGallery
    );
    const productHref = swatchLink.getAttribute('href') || undefined;
    imgBox.dataset.collectionGallery = JSON.stringify(images);

    const controller = galleryControllers.get(imgBox);
    if (controller) {
      controller.setImages(images, productHref);
      return;
    }
    if (images.length > 1 && isMobile()) enhanceGallery(card);
  };

  // ---------- Card orchestration ----------
  const maybePlayFirstCardHint = grid => {
    if (hintPlayed || !grid) return;

    const firstCard = grid.querySelector(CARD_SELECTOR);
    if (!firstCard) return;

    enhanceSwatches(firstCard);
    const controller = enhanceGallery(firstCard, { eagerSecond: true });
    if (!controller) return;

    scheduleIdle(() => {
      if (hintPlayed) return;
      requestAnimationFrame(() => controller.playHint());
    });
  };

  const ensureGalleryObserver = () => {
    if (galleryObserver) return galleryObserver;

    galleryObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const card = entry.target;
          galleryObserver.unobserve(card);
          scheduleCardWork(() => {
            if (!isMobile()) return;
            enhanceSwatches(card);
            enhanceGallery(card);
          });
        });
      },
      { root: null, rootMargin: IO_ROOT_MARGIN, threshold: 0.01 }
    );

    return galleryObserver;
  };

  const observeCard = card => {
    if (!card || observedCards.has(card)) return;
    observedCards.add(card);
    ensureGalleryObserver().observe(card);
  };

  const initCards = root => {
    if (!isMobile()) return;

    const grid = document.querySelector(ROOT_SELECTOR);
    if (!grid) return;

    const scope = root?.querySelectorAll ? root : grid;
    const cards = Array.from(scope.querySelectorAll(CARD_SELECTOR)).filter(
      card => grid.contains(card)
    );
    const firstCard = grid.querySelector(CARD_SELECTOR);

    cards.forEach(card => {
      enhanceSwatches(card);
      // First card is enhanced immediately for the intro hint; others wait for IO.
      if (card !== firstCard) observeCard(card);
    });

    if (!hintPlayed) {
      maybePlayFirstCardHint(grid);
    } else if (firstCard && cards.includes(firstCard)) {
      enhanceGallery(firstCard, { eagerSecond: true });
    }
    // If the first card sits outside this batch (infinite scroll), only new cards get observed.
  };

  const onSwatchClick = event => {
    if (!isMobile()) return;
    const swatch = event.target.closest(
      `${ROOT_SELECTOR} .product-alt-colours a:not(.additional-color)`
    );
    if (!swatch) return;

    const card = swatch.closest('.card-wrapper');
    if (!card) return;

    setTimeout(() => {
      stripSwatchSizeClasses(
        card.querySelector('.product-alt-colours') || card
      );
      applyColorGallery(card, swatch);
    }, 0);
  };

  // Single debounced resize handler for every active gallery instead of one
  // `resize` listener per card — the main win on grids with many products.
  const onWindowResize = () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      activeControllers.forEach(c => c.handleResize());
    });
  };

  const boot = () => {
    if (!document.querySelector(ROOT_SELECTOR)) return;

    initCards(document);
    if (initialized) return;
    initialized = true;

    document.addEventListener('click', onSwatchClick, true);
    window.addEventListener('resize', onWindowResize);

    window.addEventListener('collection-infinite-scroll:hydrated', event => {
      initCards(event.detail?.container || document);
    });

    window.matchMedia(MOBILE_MQ).addEventListener('change', event => {
      if (event.matches) initCards(document);
    });
  };

  window.initCollectionCardGallery = boot;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
